import { useEffect } from 'react';
import './SurfAnimation.css';

export default function SurfAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    const elements = [];
    const timers = [];

    // PHASE 1: Water Charging (0-400ms)
    attackerEl.style.transition = 'transform 0.4s ease-in-out, filter 0.4s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    attackerEl.style.filter = 'brightness(1.3) drop-shadow(0 0 20px #1e90ff)';
    
    // Charging aura
    const chargingAura = document.createElement('div');
    chargingAura.className = 'surf-charging-aura';
    chargingAura.style.left = `${attackerCenterX}px`;
    chargingAura.style.top = `${attackerCenterY}px`;
    container.appendChild(chargingAura);
    elements.push(chargingAura);
    
    // Water droplets gathering
    for (let i = 0; i < 15; i++) {
      const dropletTimer = setTimeout(() => {
        const droplet = document.createElement('div');
        droplet.className = 'surf-water-droplet';
        const dropletAngle = (i / 15) * Math.PI * 2;
        const dropletDist = 30 + Math.random() * 25;
        droplet.style.left = `${attackerCenterX + Math.cos(dropletAngle) * dropletDist}px`;
        droplet.style.top = `${attackerCenterY + Math.sin(dropletAngle) * dropletDist}px`;
        droplet.style.setProperty('--droplet-delay', `${i * 0.025}s`);
        container.appendChild(droplet);
        elements.push(droplet);
        
        const dropletRemoveTimer = setTimeout(() => {
          if (droplet.isConnected) droplet.remove();
        }, 700);
        timers.push(dropletRemoveTimer);
      }, i * 20);
      timers.push(dropletTimer);
    }

    // PHASE 2: Wave Attack (400-1100ms)
    const waveAttackTimer = setTimeout(() => {
      // Create wave particles traveling to opponent
      const totalWaves = 30;
      for (let i = 0; i < totalWaves; i++) {
        const waveTimer = setTimeout(() => {
          const wave = document.createElement('div');
          wave.className = 'surf-wave-particle';
          wave.style.left = `${attackerCenterX}px`;
          wave.style.top = `${attackerCenterY}px`;
          
          const progress = 0.2 + (i / totalWaves) * 0.6;
          const waveDistance = distance * progress;
          const waveX = Math.cos(angle) * waveDistance;
          const waveY = Math.sin(angle) * waveDistance;
          const spreadX = waveX + (Math.random() - 0.5) * 15;
          const spreadY = waveY + (Math.random() - 0.5) * 15;
          
          const waveSize = 18 + Math.random() * 22;
          const travelTime = 350 + Math.random() * 120;
          
          wave.style.setProperty('--wave-target-x', `${spreadX}px`);
          wave.style.setProperty('--wave-target-y', `${spreadY}px`);
          wave.style.setProperty('--wave-size', `${waveSize}px`);
          wave.style.setProperty('--wave-travel-time', `${travelTime}ms`);
          
          container.appendChild(wave);
          elements.push(wave);
          
          const waveRemoveTimer = setTimeout(() => {
            if (wave.isConnected) wave.remove();
          }, travelTime + 100);
          timers.push(waveRemoveTimer);
        }, i * 18);
        timers.push(waveTimer);
      }
    }, 400);
    timers.push(waveAttackTimer);

    // PHASE 3: Impact (1100-1700ms)
    const impactTimer = setTimeout(() => {
      // Impact explosion
      const impact = document.createElement('div');
      impact.className = 'surf-impact-explosion';
      impact.style.left = `${defenderCenterX}px`;
      impact.style.top = `${defenderCenterY}px`;
      container.appendChild(impact);
      elements.push(impact);
      
      // Defender hit reaction
      defenderEl.classList.add('surf-hit');
      defenderEl.style.filter = 'brightness(1.5) hue-rotate(200deg) saturate(1.5)';
      
      // Splash particles
      for (let i = 0; i < 20; i++) {
        const splash = document.createElement('div');
        splash.className = 'surf-splash-particle';
        const splashAngle = Math.random() * Math.PI * 2;
        const splashDist = 25 + Math.random() * 40;
        splash.style.left = `${defenderCenterX + Math.cos(splashAngle) * splashDist}px`;
        splash.style.top = `${defenderCenterY + Math.sin(splashAngle) * splashDist}px`;
        splash.style.setProperty('--splash-rotation', `${Math.random() * 360}deg`);
        splash.style.setProperty('--splash-drift-x', `${(Math.random() - 0.5) * 60}px`);
        splash.style.setProperty('--splash-drift-y', `${(Math.random() - 0.5) * 60}px`);
        container.appendChild(splash);
        elements.push(splash);
        
        const splashTimer = setTimeout(() => {
          if (splash.parentNode) splash.remove();
        }, 700 + Math.random() * 300);
        timers.push(splashTimer);
      }
      
      // Shockwaves
      for (let i = 0; i < 3; i++) {
        const shockTimer = setTimeout(() => {
          const shock = document.createElement('div');
          shock.className = 'surf-shockwave';
          shock.style.left = `${defenderCenterX}px`;
          shock.style.top = `${defenderCenterY}px`;
          shock.style.setProperty('--shock-delay', `${i * 0.1}s`);
          container.appendChild(shock);
          elements.push(shock);
          
          const shockRemoveTimer = setTimeout(() => {
            if (shock.isConnected) shock.remove();
          }, 900);
          timers.push(shockRemoveTimer);
        }, i * 90);
        timers.push(shockTimer);
      }
      
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('surf-hit');
        defenderEl.style.filter = '';
      }, 650);
      timers.push(defenderResetTimer);
    }, 1100);
    timers.push(impactTimer);

    // Reset attacker
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1300);
    timers.push(resetTimer);

    // Complete animation
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1800);
    timers.push(completeTimer);
    
    // Cleanup
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('surf-hit');
        defenderEl.style.filter = '';
      }
      
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      const remainingElements = container.querySelectorAll(
        '.surf-charging-aura, .surf-water-droplet, .surf-wave-particle, ' +
        '.surf-impact-explosion, .surf-splash-particle, .surf-shockwave'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SurfAnimation.moveType = 'water';
